import { Box, Divider, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import { BNButton } from '../../components/Button/Button';
import { BNSelect } from '../../components/Select/Select';
import { BNSwitch } from '../../components/Switch/Switch';
import {
  BrokerNerdsBarkerCoreModelsAdministrativePrincipalSettings,
  SystemTextJsonPatchOperationsOperation,
  getGetApiPrincipalsSelfQueryKey,
  usePatchApiPrincipalsSelf,
} from '../../api';
import { useGlobalState } from '../../data/Global.state';
import { AdminContentHeader } from '../../hoc/Admin/AdminContentHeader';
import {
  IDLE_LOGOUT_OPTION_NONE,
  IDLE_LOGOUT_OPTIONS,
  getIdleLogoutLabel,
  getIdleLogoutSelectValue,
  getIdleLogoutTimespan,
} from '../../utils/idle-logout';

export default function SecuritySettings() {
  const { principal } = useGlobalState('principal');
  const queryClient = useQueryClient();
  const { mutateAsync: updateSelf, isPending: isSaving } = usePatchApiPrincipalsSelf();
  const hasMfaFlag = useFlag('mfa');
  const hasIdleLogoutFlag = useFlag('idle-logout');
  const settings = principal?.settings ?? ({} as BrokerNerdsBarkerCoreModelsAdministrativePrincipalSettings);
  const form = useForm({
    initialValues: {
      isMfaEnabled: principal?.isMfaEnabled ?? false,
      idleLogoutDuration: getIdleLogoutSelectValue(settings.idleLogoutDuration),
    },
  });

  if (!principal) {
    return null;
  }

  const isMfaAllowed = hasMfaFlag && (!principal.sourceSystem || principal.sourceSystem === 'brokernerds' || principal.sourceSystem === 'dti');

  const resetForm = () => {
    form.setValues({
      isMfaEnabled: principal.isMfaEnabled ?? false,
      idleLogoutDuration: getIdleLogoutSelectValue(principal.settings?.idleLogoutDuration),
    });
  };

  const visibleSections = [isMfaAllowed, hasIdleLogoutFlag].filter(Boolean).length;

  return (
    <>
      <AdminContentHeader
        title="Security"
        sticky
        rightSection={
          <>
            <BNButton size="xs" variant="default" disabled={!form.isDirty() || isSaving} onClick={resetForm}>
              Cancel
            </BNButton>
            <BNButton
              size="xs"
              variant="filled"
              color="green"
              disabled={!form.isDirty()}
              loading={isSaving}
              onClick={() =>
                form.onSubmit((values) => {
                  const changes: SystemTextJsonPatchOperationsOperation[] = [];

                  if (form.isDirty('isMfaEnabled')) {
                    changes.push({ op: 'Replace', path: '/isMfaEnabled', value: values.isMfaEnabled });
                  }

                  if (hasIdleLogoutFlag && form.isDirty('idleLogoutDuration')) {
                    const newSettings: BrokerNerdsBarkerCoreModelsAdministrativePrincipalSettings = {
                      ...settings,
                      idleLogoutDuration: getIdleLogoutTimespan(values.idleLogoutDuration),
                    };

                    changes.push({ op: 'Replace', path: '/settings', value: newSettings });
                  }

                  if (changes.length === 0) {
                    return;
                  }

                  updateSelf(
                    {
                      data: changes,
                    },
                    {
                      onSuccess: (data) => {
                        queryClient.setQueryData(getGetApiPrincipalsSelfQueryKey(), () => data);
                      },
                    },
                  ).then((data) => {
                    const response = data.data;

                    form.resetDirty({
                      isMfaEnabled: response.isMfaEnabled,
                      idleLogoutDuration: getIdleLogoutSelectValue(response.settings?.idleLogoutDuration),
                    });
                  });
                })()
              }
            >
              Save
            </BNButton>
          </>
        }
      />
      <Box py="xl">
        {isMfaAllowed && (
          <Box>
            <Title order={2} size="h4" pt="sm">
              Multi-factor Authentication
            </Title>
            <Text size="xs" c="var(--colors-gray-5)">
              Add an extra verification step when you sign in to help secure your account.
            </Text>
            <Box mt="lg">
              <BNSwitch size="sm" outlined label="Require Multi-factor Authentication" checked={form.values.isMfaEnabled} {...form.getInputProps('isMfaEnabled', { type: 'checkbox' })} />
            </Box>
          </Box>
        )}

        {isMfaAllowed && hasIdleLogoutFlag && <Divider color="var(--colors-gray-1)" my="xl" />}

        {hasIdleLogoutFlag && (
          <Box>
            <Title order={2} size="h4">
              Idle Logout
            </Title>
            <Text size="xs" c="var(--colors-gray-5)">
              Automatically sign out after a period of inactivity across all of your open pricer tabs.
            </Text>
            <Box mt="lg">
              <BNSelect size="xs" maw={280} label="Idle Logout Time" data={IDLE_LOGOUT_OPTIONS.map((option) => ({ label: option.label, value: option.value }))} {...form.getInputProps('idleLogoutDuration')} />
            </Box>
            <Text size="xs" c="var(--colors-gray-5)" mt="xs">
              {form.values.idleLogoutDuration === IDLE_LOGOUT_OPTION_NONE
                ? 'Your session will stay signed in until you log out or it expires normally.'
                : `You will be signed out after ${getIdleLogoutLabel(form.values.idleLogoutDuration).toLowerCase()} of inactivity.`}
            </Text>
          </Box>
        )}

        {visibleSections === 0 && (
          <Text size="xs" c="var(--colors-gray-5)">
            Additional security settings are not available for your account yet.
          </Text>
        )}
      </Box>
    </>
  );
}
